/*
 * O estado da IA que o servidor manda (`canelaAi`, por pessoa) e os
 * atalhos para os pedidos à API — os comuns (JSON) e os que chegam em
 * streaming (NDJSON, uma linha por evento).
 *
 * `canelaAi` é null quando a IA está desligada, sem provedor ou sem as
 * tabelas: aí nada de IA aparece no fórum.
 */
import app from 'flarum/forum/app';
export function aiInfo() {
  return app.forum?.attribute('canelaAi') ?? null;
}

/** O recurso vale para todos (resumos prontos, gists, relacionados). */
export function aiFeature(feature) {
  return !!aiInfo()?.[feature];
}

/** O recurso vale e a pessoa pode usá-lo (bot, busca, ajudante). */
export function aiUsable(feature) {
  const info = aiInfo();
  return !!info && !!info[feature] && info.canUse && !!app.session.user;
}
export function aiApiUrl(path) {
  return `${app.forum.attribute('apiUrl')}/canela/ai/${path}`;
}
export function aiRequest(method, path, body) {
  return app.request({
    method,
    url: aiApiUrl(path),
    body
  });
}
/**
 * POST que lê a resposta enquanto ela é escrita.
 *
 * O `app.request` só entrega o corpo inteiro, no fim; aqui cada linha (um
 * objeto JSON completo) vai para `onLine` assim que chega — é o que dá o
 * efeito de "digitando". Um erro antes do stream (422 do limite,
 * 403…) vira uma exceção com a mensagem da API, para o alerta.
 */
export async function aiStream(path, body, onLine, signal) {
  const response = await fetch(aiApiUrl(path), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
      'X-CSRF-Token': app.session.csrfToken
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
    signal
  });
  if (!response.ok || !response.body) {
    let message = '';
    try {
      const data = await response.json();
      message = data?.errors?.[0]?.detail || '';
    } catch {
      // Corpo que não é JSON: fica a mensagem genérica.
    }
    throw new AiStreamError(response.status, message);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  for (;;) {
    const {
      done,
      value
    } = await reader.read();
    if (done) break;
    buffered += decoder.decode(value, {
      stream: true
    });

    // O que vem depois da última quebra de linha é meia linha: espera o
    // próximo pedaço.
    const lines = buffered.split('\n');
    buffered = lines.pop() ?? '';
    for (const line of lines) {
      if (line.trim() === '') continue;
      try {
        onLine(JSON.parse(line));
      } catch (error) {
        console.warn('[canela] linha inválida no stream da IA', error);
      }
    }
  }
  if (buffered.trim() !== '') {
    try {
      onLine(JSON.parse(buffered));
    } catch {
      // Uma linha cortada no fim (conexão caiu) não tem o que mostrar.
    }
  }
}
export class AiStreamError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

/** O alerta de erro de um pedido à IA, com a mensagem da API quando houver. */
export function showAiError(error) {
  if (error?.name === 'AbortError') return;
  const message = error instanceof AiStreamError ? error.message : error?.response?.errors?.[0]?.detail;

  // Os erros do app.request já viram alerta sozinhos; os do fetch, não.
  if (error instanceof AiStreamError || !error?.alert) {
    app.alerts.show({
      type: 'error'
    }, message || app.translator.trans('ramon-canela.forum.ai.errors.generic'));
  }
}
flarum.reg.add('ramon-canela', 'forum/features/ai/state', { aiInfo: aiInfo,aiFeature: aiFeature,aiUsable: aiUsable,aiApiUrl: aiApiUrl,aiRequest: aiRequest,AiStreamError: AiStreamError,showAiError: showAiError, });